import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import escapeRegExp from "lodash/escapeRegExp";
import * as z from "zod";
import {
  AdvancedFilterType,
  createAdvancedFilterDataSchema,
  GenericFilterBuilder,
} from "./generic-advanced-filter-data";

export enum ArrayStringFilterOperator {
  CONTAINS = "contains",
  ALL_OF = "all_of",
  ANY_OF = "any_of",
  NONE_OF = "none_of",
  NOT_CONTAINS = "not_contains",
  IS_EMPTY = "is_empty",
  IS_NOT_EMPTY = "is_not_empty",
  STARTS_WITH = "starts_with",
  DOES_NOT_START_WITH = "does_not_start_with",
  /** One contiguous phrase, in the order typed (`match_phrase`). */
  MATCHES_EXACTLY = "matches_exactly",
}
export const valuelessOperators = [
  ArrayStringFilterOperator.IS_EMPTY,
  ArrayStringFilterOperator.IS_NOT_EMPTY,
];

/** Operators whose value is typed, not chosen from a list of options. */
export const textInputOperators = [
  ArrayStringFilterOperator.CONTAINS,
  ArrayStringFilterOperator.NOT_CONTAINS,
  ArrayStringFilterOperator.MATCHES_EXACTLY,
  ArrayStringFilterOperator.STARTS_WITH,
  ArrayStringFilterOperator.DOES_NOT_START_WITH,
];

const ArrayStringFilterOperatorSchema = createEnum(ArrayStringFilterOperator, {
  enumName: "ArrayStringFilterOperator",
}).meta({ privacy: privacy.nonPii });

const ArrayStringFilterValueSchema = z
  .array(z.string())
  .meta({ privacy: privacy.null });

export const ArrayStringFilterDataSchema = createAdvancedFilterDataSchema(
  AdvancedFilterType.ARRAY_TO_STRING,
  ArrayStringFilterValueSchema,
  ArrayStringFilterOperatorSchema,
);
export type ArrayStringFilterData = z.infer<typeof ArrayStringFilterDataSchema>;

export const ArrayStringFilterBuilder: GenericFilterBuilder<
  string[],
  ArrayStringFilterOperator,
  ArrayStringFilterData
> = {
  type: AdvancedFilterType.ARRAY_TO_STRING,
  valueSchema: ArrayStringFilterValueSchema,
  operatorSchema: ArrayStringFilterOperatorSchema,
  schema: ArrayStringFilterDataSchema,
  addOpensearchFilters: ({
    filter,
    opensearchPath,
    keywordPath,
    compound,
    idFormat,
  }) => {
    // Convert filter values using idFormat if provided (e.g., loc_ABC123 -> bigint string)
    const filterValues = idFormat
      ? filter.value?.map((v) => {
          try {
            return idFormat.read(v).toString();
          } catch {
            // If ID format is invalid, use the raw value
            return v;
          }
        })
      : filter.value;

    if (filterValues && filterValues.length > 0) {
      if (filter.operator === ArrayStringFilterOperator.CONTAINS) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const truncatedSearch = filterValues[0].substring(0, 200).trim();
        if (!keywordPath) {
          throw new Error(
            `keywordPath is missing in ${filter.name} but required for CONTAINS`,
          );
        }
        if (truncatedSearch) {
          compound.filter.push({
            wildcard: { [keywordPath]: `*${escapeRegExp(truncatedSearch)}*` },
          });
        }
      }
      if (filter.operator === ArrayStringFilterOperator.NOT_CONTAINS) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const truncatedSearch = filterValues[0].substring(0, 200).trim();
        if (!keywordPath) {
          throw new Error(
            `keywordPath is missing in ${filter.name} but required for NOT_CONTAINS`,
          );
        }
        if (truncatedSearch) {
          compound.must_not.push({
            wildcard: { [keywordPath]: `*${escapeRegExp(truncatedSearch)}*` },
          });
        }
      }
      if (filter.operator === ArrayStringFilterOperator.MATCHES_EXACTLY) {
        const truncatedSearch = filterValues[0]?.substring(0, 200).trim();
        if (truncatedSearch) {
          compound.filter.push({
            match_phrase: { [opensearchPath]: truncatedSearch },
          });
        }
      }
      if (filter.operator === ArrayStringFilterOperator.ALL_OF) {
        if (!keywordPath) {
          throw new Error(
            `keywordPath is missing in ${filter.name} but required for ALL_OF`,
          );
        }
        for (const value of filterValues) {
          compound.must.push({ term: { [keywordPath]: value } });
        }
      }
      if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
        if (!keywordPath) {
          throw new Error(
            `keywordPath is missing in ${filter.name} but required for NONE_OF`,
          );
        }
        compound.must_not.push({ terms: { [keywordPath]: filterValues } });
      }
      if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
        if (!keywordPath) {
          throw new Error(
            `keywordPath is missing in ${filter.name} but required for ANY_OF`,
          );
        }
        compound.filter.push({ terms: { [keywordPath]: filterValues } });
      }
      if (filter.operator === ArrayStringFilterOperator.STARTS_WITH) {
        if (!keywordPath) {
          throw new Error(
            `keywordPath is missing in ${filter.name} but required for STARTS_WITH`,
          );
        }
        // This should always only have one value
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        if (filterValues.length === 1 && filterValues[0]?.length > 0) {
          compound.filter.push({ prefix: { [keywordPath]: filterValues[0] } });
        }
      }
      if (filter.operator === ArrayStringFilterOperator.DOES_NOT_START_WITH) {
        if (!keywordPath) {
          throw new Error(
            `keywordPath is missing in ${filter.name} but required for DOES_NOT_START_WITH`,
          );
        }
        // This should always only have one value
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        if (filterValues.length === 1 && filterValues[0]?.length > 0) {
          compound.must_not.push({
            prefix: { [keywordPath]: filterValues[0] },
          });
        }
      }
    }

    if (filter.operator === ArrayStringFilterOperator.IS_EMPTY) {
      const emptyEqualityPath = keywordPath ?? opensearchPath;
      compound.filter.push({
        bool: {
          should: [
            { bool: { must_not: [{ exists: { field: opensearchPath } }] } },
            { term: { [emptyEqualityPath]: "" } },
          ],
          minimum_should_match: 1,
        },
      });
    }
    if (filter.operator === ArrayStringFilterOperator.IS_NOT_EMPTY) {
      if (!keywordPath) {
        throw new Error(
          `keywordPath is missing in ${filter.name} but required for IS_NOT_EMPTY`,
        );
      }
      compound.filter.push({ wildcard: { [keywordPath]: "?*" } });
    }
  },
  readFromString: (name, operatorValueEncoded, defaultFilter) => {
    try {
      const operatorValue = operatorValueEncoded.split("::");

      const operator = operatorValue?.[0];
      const encodedValue = operatorValue?.[1];

      if (!operator) {
        return defaultFilter;
      }

      const parsedOperator =
        ArrayStringFilterOperatorSchema.safeParse(operator);
      if (!parsedOperator.success) {
        console.warn("Invalid operator type");
        return undefined;
      }

      if (!encodedValue) {
        return {
          type: AdvancedFilterType.ARRAY_TO_STRING,
          name,
          value: [],
          operator: parsedOperator.data,
        };
      }

      const decodedValues = encodedValue
        .split(",")
        .map((v) => decodeURIComponent(v));

      const valueParsed = ArrayStringFilterValueSchema.safeParse(decodedValues);

      if (!valueParsed.success) {
        console.warn("Invalid value type for ArrayStringFilter");
        return undefined;
      }

      return {
        type: AdvancedFilterType.ARRAY_TO_STRING,
        name,
        value: valueParsed.data,
        operator: parsedOperator.data,
      };
    } catch (error) {
      console.error("Error parsing ArrayStringFilter", error);
      return undefined;
    }
  },
  writeToString: (filter: ArrayStringFilterData | undefined) => {
    try {
      if (!filter) {
        return "";
      }
      const { operator, value } = filter;
      const parsedValue = ArrayStringFilterValueSchema.safeParse(value);

      if (parsedValue.success) {
        const encodedValues = parsedValue.data
          .map((v) => encodeURIComponent(v))
          .join(",");
        return `${operator}::${encodedValues}`;
      } else {
        return `${operator}::`;
      }
    } catch (error) {
      console.error("Error writing ArrayStringFilter", error);
      return "";
    }
  },
  isPartial: (filter) => {
    if (!filter.operator) {
      return true;
    }

    if (valuelessOperators.includes(filter.operator)) {
      return false;
    }

    return !filter.value || filter.value.length === 0;
  },
};
